import { ApparatusComponent } from "apparatus/component";
import { P, RP } from "apparatus/library/common";
import ArrowComponent from "arrows/component";
import { commandProcessor } from "command/command";
import { exportFromCanvas } from "editing/image_export";
import { Move } from "editing/move";
import { RenderAreaBox } from "editing/render_area";
import { clampZoomValue } from "editing/zoom";
import { focusMainApp } from "keyboard";
import { EditorContextMenu } from "layout/editor/context_menu";
import { GlobalSnapVizView } from "layout/editor/global_snap_viz";
import { setUpInitialDiagram } from "layout/editor/initial_diagram";
import { clog } from "log";
import { Size } from "paper";
import * as React from "react";
import { Component, createElement } from "react";
import { ID } from "store/id";
import store from "store/store";
import { NULL_SELECTION_METRICS } from "types";
import { Consts } from "vars";
import { isMac } from "view/keyboard_shortucts";
import { ZoomCommand } from "view/zoom";
import { exportFromCanvasJPGThumbnail } from "../../editing/image_export";
import { isItemUnlocked } from "../../tiers/apparatus";
import { toolCursors } from "../../tools";
const MAC_DRAG_FACTOR = 0.5;
const MAC_PINCH_FACTOR = 0.01;
const DEBOUNCE_DURATION_MS = 40;
const maxPixels = (tier) => {
    if (!tier)
        return Consts.pngMaxRenderPixelsMedium;
    switch (tier) {
        case "boost":
        case "plus":
            return Consts.pngMaxRenderPixels;
    }
};
/**
 * Component that holds the canvas and everything that's drawn
 * inside it, including apparatus components.
 */
export class EditorCanvasComponent extends Component {
    constructor(props) {
        super(props);
        /** Canvas element ref. */
        this.canvasElementRef = React.createRef();
        // TODO: Avoid this by using states?
        /** Refs to drawable components (apparatus, arrows..). */
        this.objectRefs = {};
        this.renderAreaRef = React.createRef();
        this.globalSnapVizViewRef = React.createRef();
        this.state = { isPaperReady: false };
    }
    getApparatusComponent(id) {
        return this.objectRefs[ID.string(id)];
    }
    getObjectComponent(id) {
        return this.objectRefs[ID.string(id)];
    }
    componentDidMount() {
        // Set up canvas
        const canvas = this.canvasElementRef.current;
        paper.setup(canvas);
        this.setState({ isPaperReady: true });
        clog("Paper.js set up");
        this.props.onPaperReady();
        if (!Consts.isDev) {
            setUpInitialDiagram();
        }
        // Set up layers
        let layers = {
            exportOnlyBacking: new paper.Layer(),
            apparatus: new paper.Layer(),
            arrows: new paper.Layer(),
            overlay: new paper.Layer(),
            watermark: new paper.Layer(),
        };
        this.layers = layers;
        // Set the apparatus layer as default.
        layers.apparatus.activate();
    }
    setZoom(zoom) {
        paper.project.view.zoom = zoom;
    }
    getProjectBounds() {
        // Compute bounds of the diagram from individual object bounds.
        let ids = [
            ...this.props.arrows.map(arrow => arrow.id),
            ...this.props.items.map(item => item.id)
        ];
        let items = ids.map(id => this.objectRefs[ID.string(id)]);
        if (items.length == 0)
            return new paper.Rectangle(P(0, 0), new Size(0, 0));
        let bounds = new paper.Rectangle(items[0].bounds);
        for (let item of items) {
            bounds.united(item.bounds);
        }
        return bounds;
    }
    export(options) {
        const pixels = maxPixels(this.props.tier);
        let bounds;
        if (this.props.renderArea) {
            const renderArea = this.props.renderArea;
            bounds = RP(renderArea.left, renderArea.top, renderArea.right, renderArea.bottom).expand(2);
        }
        else {
            bounds = this.getProjectBounds();
        }
        return exportFromCanvas(paper.project, this.layers, bounds, pixels, options);
    }
    exportThumbnail() {
        return exportFromCanvasJPGThumbnail(paper.project, this.layers, this.getProjectBounds());
    }
    componentDidUpdate() {
        // Reorder drawn items to match the order in the data store.
        this.ensureVisualOrder();
    }
    onCanvasMouseDownOrTouchStart() {
        // Return focus to the app.
        focusMainApp();
    }
    render() {
        var _a;
        // Set zoom on paper project.
        if (this.state.isPaperReady) {
            this.setZoom(this.props.zoom);
        }
        // Render main canvas.
        let canvas = createElement("canvas", {
            key: "MainCanvas",
            className: "editor",
            style: {
                "cursor": (_a = (this.props.tool == "select" ? this.state.renderAreaBoxCursorHint : null)) !== null && _a !== void 0 ? _a : toolCursors(this.props.tool),
            },
            resize: "true",
            ref: this.canvasElementRef,
            onMouseDown: e => this.onCanvasMouseDownOrTouchStart(),
            onTouchStart: e => this.onCanvasMouseDownOrTouchStart(),
            onWheel: e => this.onWheel(e),
            onContextMenu: e => {
                e.preventDefault();
                this.setState({ contextMenu: { position: { x: e.clientX, y: e.clientY } } });
                return false;
            }
        });
        // Render apparatus components.
        let apparatusComponents = this.props.items.map(item => React.createElement(ApparatusComponent
        // Use a key that includes the ID as well as type.
        // ApparatusComponent once set up for a specific
        // apparatus type can't be reused for another type.
        , Object.assign({ 
            // Use a key that includes the ID as well as type.
            // ApparatusComponent once set up for a specific
            // apparatus type can't be reused for another type.
            key: ID.string(item.id) + "/" + item.type, isWatermarkEnabled: !isItemUnlocked(item, this.props.tier), zoom: this.props.zoom, isInteracting: this.props.isInteracting, ref: r => r
                ? this.objectRefs[ID.string(item.id)] = r
                : delete this.objectRefs[ID.string(item.id)] }, item)));
        // Render arrows.
        let arrows = this.props.arrows.map(arrow => React.createElement(ArrowComponent, Object.assign(Object.assign({}, arrow), { key: ID.string(arrow.id), ref: r => this.objectRefs[ID.string(arrow.id)] = r, zoom: this.props.zoom, showAdjustables: this.props.tool == "select"
                && arrow.selected
                && !(this.props.isInteracting == "zoom") })));
        // Temporary arrow, one that the user is currently drawing.
        let currentlyDrawnArrow = this.state.arrow
            ? React.createElement(ArrowComponent, Object.assign(Object.assign({ key: "__DrawnArrow" }, this.state.arrow), { showAdjustables: false, zoom: this.props.zoom }))
            : null;
        // Wait until paper is ready to draw everything. Otherwise, only draw the canvas
        // so that paper can initialise itself.
        if (!this.state.isPaperReady) {
            return [canvas];
        }
        return [
            canvas,
            React.createElement(EditorContextMenu, { key: "ContextMenu", contextMenu: this.state.contextMenu, onClose: () => this.setState({ contextMenu: undefined }), selected: this.props.selected }),
            this.props.renderArea ?
                React.createElement(RenderAreaBox, Object.assign({ key: "Renderbox", layer: this.layers.overlay }, this.props.renderArea, { interactable: this.props.tool == "select", ref: this.renderAreaRef, zoom: this.props.zoom, setCursorHint: h => { this.setState({ renderAreaBoxCursorHint: h }); } })) :
                undefined,
            // The following are "virtual components", they don't
            // contain any DOM, because the drawing is handled
            // within the components, via paper.
            currentlyDrawnArrow,
            ...apparatusComponents,
            ...arrows,
            React.createElement(GlobalSnapVizView, { key: "GlobalSnapViz", ref: this.globalSnapVizViewRef }),
        ];
    }
    onWheel(e) {
        // Different behaviour depending on platform:
        if (isMac() || true) {
            if (e.ctrlKey) {
                // Zoom. Pinch in => Positive. Pinch out => negative
                const newZoom = clampZoomValue(this.props.zoom - e.deltaY * MAC_PINCH_FACTOR);
                commandProcessor.executeReplacingLastCommand((lastCommand) => {
                    if (!(lastCommand instanceof ZoomCommand))
                        return false;
                    return new ZoomCommand(lastCommand.from, newZoom);
                }, new ZoomCommand(this.props.zoom, newZoom));
            }
            else {
                // Drag
                // Natural: Drag Down => negative, drag (left to) right => negative
                let delta = P(-e.deltaX, -e.deltaY).multiply(MAC_DRAG_FACTOR);
                if (this.debounceTimeoutHandle) {
                    this.debouncedDragDelta = this.debouncedDragDelta.add(delta);
                }
                else {
                    this.debouncedDragDelta = delta;
                    this.debounceTimeoutHandle = window.setTimeout(() => {
                        let allIds = [...store.items().map(i => i.id), ...store.arrows().map(a => a.id)];
                        // TODO: Support Render area Frames
                        commandProcessor.executeReplacingLastCommand((lastCommand) => {
                            if (!(lastCommand instanceof Move))
                                return false;
                            return new Move(allIds, lastCommand.delta.add(this.debouncedDragDelta));
                        }, new Move(allIds, this.debouncedDragDelta));
                        clearTimeout(this.debounceTimeoutHandle);
                        this.debounceTimeoutHandle = undefined;
                    }, DEBOUNCE_DURATION_MS);
                }
            }
        }
        else {
            // Scroll wheel zooms only. Up => negative deltaY
            const newZoom = clampZoomValue(this.props.zoom - e.deltaY / 1000);
            commandProcessor.executeReplacingLastCommand((lastCommand) => {
                if (!(lastCommand instanceof ZoomCommand))
                    return false;
                return new ZoomCommand(lastCommand.from, newZoom);
            }, new ZoomCommand(this.props.zoom, newZoom));
        }
    }
    /**
     * Ensure that paper items order matches the order in data store. Higher index
     * in the data store implies the apparatus is drawn later/above lower index
     * apparatus. (index 0 => drawn first | at the bottom)
     */
    ensureVisualOrder() {
        // Reorder Paper elements according to position.
        for (var i = 1; i < this.props.items.length; i++) {
            let id = ID.string(this.props.items[i].id);
            let prevId = ID.string(this.props.items[i - 1].id);
            let component = this.objectRefs[id];
            let previousComponent = this.objectRefs[prevId];
            component.item.moveAbove(previousComponent.item);
        }
    }
    /** Compute the bounds on the current active selection. */
    computeSelectionMetrics() {
        var _a;
        let ids = [...this.props.selected.arrows.map(arrow => arrow.id), ...this.props.selected.items.map(item => item.id)];
        let items = ids.map(id => this.getObjectComponent(id));
        if (items.length == 0) {
            return NULL_SELECTION_METRICS;
        }
        if (items.length == 1) {
            const item = items[0];
            // For certain items, calculate the bounds center and use that as the pivot.
            const shouldUseBoundsCenter = (item instanceof ApparatusComponent) ? (_a = item.apparatus.shouldUseBoundsCenter) !== null && _a !== void 0 ? _a : false : false;
            // Only one selected apparatus, take its natural position as pivot
            // and bounds.
            return {
                pivot: shouldUseBoundsCenter ? items[0].bounds.center : items[0].currentPosition,
                bounds: items[0].bounds
            };
        }
        else {
            // Multiple items. Calculate the encompassing bounds
            // and take its center as pivot.
            let encompassingBounds = new paper.Rectangle(items[0].bounds);
            for (let item of items) {
                encompassingBounds.united(item.bounds);
            }
            return {
                pivot: encompassingBounds.center,
                bounds: encompassingBounds,
            };
        }
    }
}
